import { FC } from 'react';
import { ButtonTheme } from '../buttons/button-theme';
import Button from '../buttons/button';

export interface ModalProps {
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonTheme?: ButtonTheme;
  cancelButtonTheme?: ButtonTheme;
  isLoading?: boolean;
  children: React.ReactNode;
}

const BaseModal: FC<ModalProps> = ({
  title,
  onConfirm,
  onCancel,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  confirmButtonTheme = ButtonTheme.SUCCESS,
  cancelButtonTheme = ButtonTheme.CANCEL,
  isLoading = false,
  children
}) => {
  const triggerClick = () => {
    onConfirm();
  };

  return (
    <>
      <div
        className="relative z-50"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div className="fixed inset-0 bg-gray bg-opacity-75 transition-opacity"></div>

        <div className="fixed inset-0 z-40 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  {/* icon here */}
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-error-300 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                      />
                    </svg>
                  </div>

                  {/* content here */}
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3
                      className="text-lg font-medium leading-6 text-gray-900"
                      id="modal-title"
                    >
                      {title}
                    </h3>
                    <div className="mt-2">{children}</div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 flex flex-row-reverse gap-2">
                <Button
                  type="button"
                  theme={confirmButtonTheme}
                  identifier="confirm-button"
                  isLoading={isLoading}
                  onClick={() => triggerClick()}
                >
                  <p className={`${isLoading ? 'invisible' : ''}`}>
                    {confirmButtonText}
                  </p>
                </Button>
                <Button
                  type="button"
                  theme={cancelButtonTheme}
                  identifier="cancel-button"
                  isLoading={isLoading}
                  onClick={() => onCancel()}
                >
                  <p className={`${isLoading ? 'invisible' : ''}`}>
                    {cancelButtonText}
                  </p>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BaseModal;
